import { useCallback, useEffect, useRef, useState } from 'react'

/** Desktop réel (souris) — pas de hover sur tactile. Évalué une fois. */
function canHover(): boolean {
    return (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches
    )
}

/**
 * Ouverture au survol d'un menu (Radix DropdownMenu) — DESKTOP UNIQUEMENT.
 * Le clic reste pleinement fonctionnel (l'état est contrôlé). Sur tactile, le
 * survol est ignoré → comportement clic inchangé.
 *
 * Usage : `<DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>`
 * puis étaler `hoverProps` sur le trigger ET sur le content (le content est
 * porté ailleurs dans le DOM : sans ses handlers, sortir du trigger fermerait
 * le menu avant qu'on l'atteigne).
 */
export function useHoverDropdown(closeDelay = 150) {
    const [open, setOpen] = useState(false)
    const hoverable = useRef(canHover())
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clear = () => {
        if (timer.current) {
            clearTimeout(timer.current)
            timer.current = null
        }
    }
    useEffect(() => clear, [])

    const onMouseEnter = useCallback(() => {
        if (!hoverable.current) return
        clear()
        setOpen(true)
    }, [])

    const onMouseLeave = useCallback(() => {
        if (!hoverable.current) return
        clear()
        timer.current = setTimeout(() => setOpen(false), closeDelay)
    }, [closeDelay])

    // Sur desktop hover, empêcher Radix de déplacer le focus à l'ouverture/fermeture
    // (sinon un focus-ring « clavier » apparaît de façon intempestive sur le trigger).
    const preventFocus = useCallback((e: Event) => {
        if (hoverable.current) e.preventDefault()
    }, [])

    const hoverProps = { onMouseEnter, onMouseLeave }
    return {
        open,
        onOpenChange: setOpen,
        hoverProps,
        /** À étaler sur le DropdownMenuContent (hover + neutralisation du focus-ring). */
        contentProps: {
            ...hoverProps,
            onOpenAutoFocus: preventFocus,
            onCloseAutoFocus: preventFocus,
        },
    }
}
